import { useEffect, useState, useMemo } from "react";
import { fetchModelMenuData } from "@libs/apiRequest";

export default function useModelData(
  currentUserRole,
  selectedProject,
  selectedTab,
) {
  const [modelMenu, setModelMenu] = useState({
    sales_model_menu: [],
    coordination_model_menu: [],
    ifc_model_menu: [],
    hvac_model_menu: [],
    design_model_menu: [],
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState({
    modelMenuData: true,
    versionData: true,
  });

  // 1. Fetch Model Menu data
  useEffect(() => {
    const fetchModelMenu = async () => {
      if (!currentUserRole) return;

      try {
        let salesModelMenuData = [];
        let coordinationModelMenuData = [];
        let ifcModelMenuData = [];
        let hvacModelMenuData = [];
        let designModelMenuData = [];

        if (
          currentUserRole === "SALES" ||
          currentUserRole?.startsWith("SALES_")
        ) {
          salesModelMenuData = await fetchModelMenuData("SALES");
        } else if (
          currentUserRole === "SITE" ||
          currentUserRole?.startsWith("SITE_")
        ) {
          [
            coordinationModelMenuData,
            ifcModelMenuData,
            hvacModelMenuData,
            designModelMenuData,
          ] = await Promise.all([
            fetchModelMenuData("COORDINATION"),
            fetchModelMenuData("IFC"),
            fetchModelMenuData("HVAC"),
            fetchModelMenuData("DESIGN"),
          ]);
        } else if (
          currentUserRole === "ADMIN" ||
          currentUserRole?.startsWith("ADMIN_") ||
          currentUserRole === "MANAGE" ||
          currentUserRole?.startsWith("MANAGE_")
        ) {
          [
            salesModelMenuData,
            coordinationModelMenuData,
            ifcModelMenuData,
            hvacModelMenuData,
            designModelMenuData,
          ] = await Promise.all([
            fetchModelMenuData("SALES"),
            fetchModelMenuData("COORDINATION"),
            fetchModelMenuData("IFC"),
            fetchModelMenuData("HVAC"),
            fetchModelMenuData("DESIGN"),
          ]);
        }

        setModelMenu({
          sales_model_menu: salesModelMenuData,
          coordination_model_menu: coordinationModelMenuData,
          ifc_model_menu: ifcModelMenuData,
          hvac_model_menu: hvacModelMenuData,
          design_model_menu: designModelMenuData,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading((prevState) => ({
          ...prevState,
          modelMenuData: false,
        }));
      }
    };

    fetchModelMenu();
  }, [currentUserRole]); // rerun when role changes

  // 2. Map selected project's models to model menu data based on ids
  const modelMap = useMemo(() => {
    if (
      !Array.isArray(selectedProject?.models) ||
      !modelMenu?.sales_model_menu ||
      !modelMenu?.coordination_model_menu ||
      !modelMenu?.ifc_model_menu ||
      !modelMenu?.hvac_model_menu ||
      !modelMenu?.design_model_menu
    ) {
      console.warn("Models or modelMenu not ready yet");
      return {
        sales_model_list: [],
        coordination_model_list: [],
        ifc_model_list: [],
        hvac_model_list: [],
        design_model_list: [],
      };
    }

    const salesModels = [];
    const coordModels = [];
    const ifcModels = [];
    const hvacModels = [];
    const designModels = [];

    try {
      selectedProject.models.forEach(
        ({
          salesModelId,
          coordinationModelId,
          ifcModelId,
          hvacModelId,
          designModelId,
        }) => {
          // Sales model (optional)
          if (salesModelId) {
            const found = modelMenu.sales_model_menu.find(
              (model) => model.id === salesModelId,
            );
            if (found) salesModels.push(found);
          }

          // Coordination model (optional)
          if (coordinationModelId) {
            const found = modelMenu.coordination_model_menu.find(
              (model) => model.id === coordinationModelId,
            );
            if (found) coordModels.push(found);
          }

          // IFC model (optional)
          if (ifcModelId) {
            const found = modelMenu.ifc_model_menu.find(
              (model) => model.id === ifcModelId,
            );
            if (found) ifcModels.push(found);
          }

          // HVAC model (optional)
          if (hvacModelId) {
            const found = modelMenu.hvac_model_menu.find(
              (model) => model.id === hvacModelId,
            );
            if (found) hvacModels.push(found);
          }

          // Design model (optional)
          if (designModelId) {
            const found = modelMenu.design_model_menu.find(
              (model) => model.id === designModelId,
            );
            if (found) designModels.push(found);
          }
        },
      );
    } catch (err) {
      console.error("❌ Error mapping models:", err);
      setError(err.message);
    }

    return {
      sales_model_list: salesModels,
      coordination_model_list: coordModels,
      ifc_model_list: ifcModels,
      hvac_model_list: hvacModels,
      design_model_list: designModels,
    };
  }, [selectedProject, modelMenu]);

  // 3. Setup default version for modelMap
  const selectedVersions = useMemo(() => {
    const setDefaultVersions = (modelList) => {
      if (modelList.length === 0) return {};

      let defaultVersions = {};
      modelList.forEach((singleModel) => {
        defaultVersions[singleModel.id] = singleModel.defaultVersion;
      });
      return defaultVersions;
    };

    return {
      sales_model_version: setDefaultVersions(modelMap.sales_model_list),
      coordination_model_version: setDefaultVersions(
        modelMap.coordination_model_list,
      ),
      ifc_model_version: setDefaultVersions(modelMap.ifc_model_list),
      hvac_model_version: setDefaultVersions(modelMap.hvac_model_list),
      design_model_version: setDefaultVersions(modelMap.design_model_list),
    };
  }, [modelMap]);

  // 4. Set active list by tab
  const activeList = useMemo(() => {
    const activeList =
      selectedTab === "SALES"
        ? modelMap.sales_model_list
        : selectedTab === "COORDINATION"
          ? modelMap.coordination_model_list
          : selectedTab === "IFC"
            ? modelMap.ifc_model_list
            : selectedTab === "HVAC"
              ? modelMap.hvac_model_list
              : selectedTab === "DESIGN"
                ? modelMap.design_model_list
                : [];
    return activeList;
  }, [selectedTab, modelMap]);

  return {
    selectedVersions,
    activeList,
    error,
    isLoading,
  };
}
